import {
  TWebformCustomValidators,
  TWebformDefaultFieldValues,
  TWebformNormalizedStateMessages,
} from '../../types/form'
import FormMappingFields from '../../../components/form/formMappingFields/formMappingFields'
import * as yup from 'yup'
import { resolveFieldMessages } from '../webform_yup_functions/webform_yup_functions'
import { FieldValidateProps } from '../../types/components/validate'

export const checkVisibilityCondition = (
  watched: any,
  expectedValue: any,
  mode: 'is' | 'isNot' = 'is'
): boolean => {
  if (mode === 'is') {
    return watched === expectedValue
  }
  if (mode === 'isNot') {
    return watched !== expectedValue
  }
  return false
}

export function shouldFieldBeVisible(
  fieldKey: string,
  elementsSource: Record<string, any>,
  watchedValues: Record<string, string>
): boolean {
  const fieldConfig = elementsSource[fieldKey]
  const visibleStates = fieldConfig?.['#states']?.visible
  if (!visibleStates) return true

  const evaluate = (states: Record<string, any>) =>
    Object.entries(states).every(([selector, conditions]) => {
      const match = selector.match(/:input\[name="([^"]+)"\]/)
      if (!match) return true
      const depName = match[1]
      const watched = watchedValues[depName]
      if (watched === undefined) return false

      if ('value' in conditions) {
        return checkVisibilityCondition(watched, conditions.value, 'is')
      }
      if ('!value' in conditions) {
        return checkVisibilityCondition(watched, conditions['!value'], 'isNot')
      }

      return true
    })

  if (!Array.isArray(visibleStates)) {
    return evaluate(visibleStates as Record<string, any>)
  }

  return visibleStates.some((stateCond: any) => {
    if (typeof stateCond !== 'object' || stateCond === null) return false
    return evaluate(stateCond as Record<string, any>)
  })
}

export type TDependentField = { name: string; type: string }

const LAYOUT_TYPES: ReadonlySet<string> = new Set([
  'webform_section',
  'webform_flexbox',
  'container',
  'details',
  'fieldset',
  'webform_wizard_page',
])

const MARKUP_TYPES: ReadonlySet<string> = new Set([
  'webform_actions',
  'webform_markup',
])
export function isLayoutType(type: unknown): type is string {
  return typeof type === 'string' && LAYOUT_TYPES.has(type)
}

export function isMarkupType(type: unknown): type is string {
  return typeof type === 'string' && MARKUP_TYPES.has(type)
}

export function extractVisibleFields(
  source: Record<string, any>,
  visibleKeys: string[],
  watchedValues: Record<string, any>
): Array<{ key: string; field: any }> {
  const result: Array<{ key: string; field: any }> = []

  visibleKeys.forEach((key) => {
    const field = source[key]
    if (!field) return

    const type = field['#type']
    if (isLayoutType(type)) {
      const childFields = Object.fromEntries(
        Object.entries(field).filter(([k]) => !k.startsWith('#'))
      )

      const childVisibleKeys = Object.keys(childFields).filter((childKey) =>
        shouldFieldBeVisible(childKey, childFields, watchedValues)
      )

      result.push(
        ...extractVisibleFields(childFields, childVisibleKeys, watchedValues)
      )
    } else {
      result.push({ key, field })
    }
  })

  return result
}

export function getDependentFields(
  elementsSource: Record<string, any>
): TDependentField[] {
  const depsMap = new Map<string, string>()

  function extractDeps(source: Record<string, any>) {
    Object.values(source).forEach((fieldConfig) => {
      if (!fieldConfig || typeof fieldConfig !== 'object') return

      const visibleStates = fieldConfig?.['#states']?.visible
      if (visibleStates) {
        const statesArray = Array.isArray(visibleStates)
          ? visibleStates
          : [visibleStates]

        statesArray.forEach((stateCond: any) => {
          if (typeof stateCond !== 'object' || stateCond === null) return
          Object.keys(stateCond).forEach((selector) => {
            const match = selector.match(/:input\[name="([^"]+)"\]/)
            if (match) {
              const depName = match[1]
              const depType = source[depName]?.['#type'] || 'unknown'
              depsMap.set(depName, depType)
            }
          })
        })
      }

      const type = fieldConfig['#type']
      if (isLayoutType(type)) {
        const childFields = Object.fromEntries(
          Object.entries(fieldConfig).filter(([k]) => !k.startsWith('#'))
        )
        extractDeps(childFields)
      }
    })
  }

  extractDeps(elementsSource)

  return Array.from(depsMap.entries()).map(([name, type]) => ({ name, type }))
}

export const generateFormSchemaAndDefaults = ({
  elementsSource,
  visibleElementsKeys,
  defaultFieldValues,
  rhfDefaultFieldStateMessages,
  rhfCustomValidators,
  watchedValues = {},
}: {
  elementsSource: Record<string, any>
  visibleElementsKeys: string[]
  defaultFieldValues: Required<TWebformDefaultFieldValues>
  rhfDefaultFieldStateMessages: TWebformNormalizedStateMessages
  rhfCustomValidators?: TWebformCustomValidators
  watchedValues?: Record<string, any>
}) => {
  const defaults: Record<string, any> = {}
  const yupObjLocal: Record<string, any> = {}

  const realVisibleFields: Array<{ key: string; field: any }> =
    extractVisibleFields(elementsSource, visibleElementsKeys, watchedValues)

  realVisibleFields.forEach(({ key, field }) => {
    const rawType = field?.['#type']

    const type = rawType in FormMappingFields ? rawType : 'default'
    const required = Boolean(field?.['#required'])

    const fieldValidateProps: FieldValidateProps = {
      yupObject: yupObjLocal,
      defaultValues: defaults,
      key,
      field,
      required,
      defaultFieldValues,
      rhfDefaultFieldStateMessages,
      rhfCustomValidators,
      requiredMessage: '',
      errorMessage: '',
      minLengthMessage: '',
      maxLengthMessage: '',
    }

    const resolvedMessages = resolveFieldMessages(fieldValidateProps)

    fieldValidateProps.requiredMessage = resolvedMessages.required
    fieldValidateProps.errorMessage = resolvedMessages.error
    fieldValidateProps.minLengthMessage = resolvedMessages.minLength
    fieldValidateProps.maxLengthMessage = resolvedMessages.maxLength

    FormMappingFields[type ?? 'default']?.validator?.(fieldValidateProps)
  })

  return {
    defaultValues: defaults,
    validationSchema: yup.object().shape(yupObjLocal),
  }
}
